import { useMemo, useEffect } from 'react';
import { LabelStore } from 'app/stores/label/$labels';

export interface LabelOptionData {
  value: string;
  label: string;
  bg: string;
  color: string;
  isPreset?: boolean;
}

interface UseLabelOptionsResult {
  options: LabelOptionData[] | null;
  isLoading: boolean;
}

/**
 * Hook to fetch label options for use in filter dropdowns.
 * @param $labels - The shared label store
 * @param labelType - 'synth_agent' | 'synth_test' | 'device'
 * @param enabled - Whether to fetch/compute options
 */
export function useLabelOptions(
  $labels: LabelStore | undefined,
  labelType: 'synth_agent' | 'synth_test' | 'device',
  enabled: boolean
): UseLabelOptionsResult {
  const labels = $labels?.labels;
  const isLoading = enabled && labels?.requestStatus === 'fetching';
  const hasFetched = labels?.hasFetched;
  const lastUpdated = labels?.lastUpdated;
  const needsFetch = enabled && !!labels && !hasFetched && !labels.requestStatus;

  useEffect(() => {
    if (needsFetch) {
      labels?.fetch();
    }
  }, [needsFetch, labels]);

  const options = useMemo(() => {
    if (!enabled || !$labels) {
      return null;
    }

    // getLabels returns LabelModel[] for the given item_type
    const matchingLabels = $labels.getLabels(labelType);
    return matchingLabels.map((label: { get(attr: string): unknown; isPreset?: boolean }) => ({
      value: String(label.get('id')),
      label: String(label.get('name')),
      bg: String(label.get('color')),
      color: '#ffffff',
      isPreset: label.isPreset ?? false
    }));
  }, [enabled, $labels, labelType, hasFetched, lastUpdated]);

  return {
    options,
    isLoading: isLoading || needsFetch || false
  };
}
